import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { useLazyGetCurrencyByDealerAddressIdQuery } from '../../../../../../api/endpoints/dealerAgreementType';
import { InputText } from 'primereact/inputtext';

const DealerAgreementTypeDialog = ({ visible, data, onHide, onSave, dealerAddressId }) => {
    const { th } = useLanguage();
    const [submitted, setSubmitted] = useState(false);
    const [dateError, setDateError] = useState(false);
    const [currencyError, setCurrencyError] = useState(false);

    const [triggerGetCurrency, { isFetching: currencyLoading }] = useLazyGetCurrencyByDealerAddressIdQuery();


    const [formData, setFormData] = useState({
        id: null,
        type: 1,
        agreementType: null,
        price: null,
        curency: '',
        contractStartDate: null,
        contractEndDate: null
    });

    useEffect(() => {
        setSubmitted(false);
        setDateError(false);
        if (data) {
            setFormData({
                id: data.id || null,
                type: data.type || 1,
                agreementType: data.agreementType || null,
                price: data.price ?? null,
                curency: data.curency || '',
                contractStartDate: data.contractStartDate ? new Date(data.contractStartDate) : null,
                contractEndDate: data.contractEndDate ? new Date(data.contractEndDate) : null
            });
        } else {
            setFormData({
                id: null,
                type: 1,
                agreementType: null,
                price: null,
                curency: '',
                contractStartDate: null,
                contractEndDate: null
            });
        }
    }, [data, visible]);

    useEffect(() => {
        setCurrencyError(false);
        if (visible && dealerAddressId) {
            triggerGetCurrency(dealerAddressId)
                .unwrap()
                .then((currency) => {
                    if (currency) {
                        setFormData((prev) => ({ ...prev, curency: currency }));
                    } else {
                        setCurrencyError(true);
                    }
                })
                .catch(() => setCurrencyError(true));
        }
    }, [visible, dealerAddressId, triggerGetCurrency]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (field === 'contractStartDate' || field === 'contractEndDate') setDateError(false);
    };

    const handleSubmit = () => {
        setSubmitted(true);

        if (formData.agreementType === null || formData.price === null || !formData.curency || !formData.contractStartDate) return;

        if (formData.contractEndDate && formData.contractStartDate && formData.contractEndDate < formData.contractStartDate) {
            setDateError(true);
            return;
        }

        onSave(formData);
        onHide();
    };

    const currencyOptions = [
        { label: 'TRY', value: 'TRY' },
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' }
    ];

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <Dialog
            header={formData.id ? th('customerAddressBrand.tabs.dealerAgreementType.dialog.title.update') : th('customerAddressBrand.tabs.dealerAgreementType.dialog.title.new')}
            visible={visible}
            style={{ width: '45vw' }}
            modal
            className="p-fluid"
            onHide={onHide}
            footer={footer}
        >
            <div className="formgrid grid">
                {/* Type (Yol Yardımı - sabit) */}
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.dealerAgreementType.columns.type')}</label>
                    <Dropdown
                        value={formData.type}
                        options={[{ label: th('customerAddressBrand.tabs.dealerAgreementType.dialog.options.type.roadAssistance'), value: 1 }]}
                        onChange={(e) => handleChange('type', e.value)}
                        disabled
                        placeholder={th('customerAddressBrand.tabs.dealerAgreementType.dialog.placeholder.type')}
                        inputClassName="w-full"
                    />
                </div>

                {/* Agreement Type (Radio) */}
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.dealerAgreementType.columns.agreementType')}</label>
                    <div className="flex gap-4 mt-2">
                        <div className="flex align-items-center">
                            <RadioButton inputId="km" name="agreementType" value={1} onChange={(e) => handleChange('agreementType', e.value)} checked={formData.agreementType === 1} />
                            <label htmlFor="km" className="ml-2">
                                {th('customerAddressBrand.tabs.dealerAgreementType.dialog.options.agreementType.km')}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton inputId="hour" name="agreementType" value={2} onChange={(e) => handleChange('agreementType', e.value)} checked={formData.agreementType === 2} />
                            <label htmlFor="hour" className="ml-2">
                                {th('customerAddressBrand.tabs.dealerAgreementType.dialog.options.agreementType.hour')}
                            </label>
                        </div>
                    </div>
                    {submitted && formData.agreementType === null && <small className="p-error">{th('common.required')}</small>}
                </div>

                {/* Price */}
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.dealerAgreementType.columns.price')}</label>
                    <InputNumber
                        value={formData.price}
                        onValueChange={(e) => handleChange('price', e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        placeholder={th('customerAddressBrand.tabs.dealerAgreementType.dialog.placeholder.price')}
                        className={classNames({ 'p-invalid': submitted && formData.price === null })}
                        inputClassName="w-full"
                    />
                    {submitted && formData.price === null && <small className="p-error">{th('common.required')}</small>}
                </div>

                {/* Currency */}
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.dealerAgreementType.columns.currency')}</label>
                    <InputText
                        value={currencyLoading ? th('common.loading') : (formData.curency || '')}
                        readOnly
                        disabled
                        className="w-full"
                    />
                    {currencyError && !formData.curency && !currencyLoading && <small className="p-error">{th('customerAddressBrand.tabs.dealerAgreementType.dialog.error.currencyNotFound')}</small>}
                    {submitted && !currencyError && !formData.curency && !currencyLoading && <small className="p-error">{th('common.required')}</small>}
                </div>

                {/* Contract Start Date */}
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.dealerAgreementType.columns.contractStartDate')}</label>
                    <Calendar
                        value={formData.contractStartDate}
                        onChange={(e) => handleChange('contractStartDate', e.value)}
                        showIcon
                        dateFormat="dd.mm.yy"
                        placeholder={th('customerAddressBrand.tabs.dealerAgreementType.dialog.placeholder.contractStartDate')}
                        className={classNames({ 'p-invalid': submitted && !formData.contractStartDate })}
                        inputClassName="w-full"
                    />
                    {submitted && !formData.contractStartDate && <small className="p-error">{th('common.required')}</small>}
                </div>

                {/* Contract End Date */}
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.dealerAgreementType.columns.contractEndDate')}</label>
                    <Calendar
                        value={formData.contractEndDate}
                        onChange={(e) => handleChange('contractEndDate', e.value)}
                        showIcon
                        dateFormat="dd.mm.yy"
                        placeholder={th('customerAddressBrand.tabs.dealerAgreementType.dialog.placeholder.contractEndDate')}
                        minDate={formData.contractStartDate || null}
                        className={classNames({ 'p-invalid': dateError })}
                        inputClassName="w-full"
                    />
                    {dateError && <small className="p-error">{th('customerAddressBrand.tabs.dealerAgreementType.dialog.error.endDateBeforeStartDate')}</small>}
                </div>
            </div>
        </Dialog>
    );
};

export default DealerAgreementTypeDialog;
